import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Download as DownloadIcon } from "@mui/icons-material";

interface FreebieDownloadFormProps {
  product: any;
  onDownload: (formData: FreebieFormData) => Promise<void>;
  isLoading?: boolean;
}

export interface FreebieFormData {
  name: string;
  email: string;
  phone: string;
}

const FreebieDownloadForm: React.FC<FreebieDownloadFormProps> = ({
  product,
  onDownload,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<FreebieFormData>({
    name: "",
    email: "",
    phone: "",
  });

  const [errors, setErrors] = useState<Partial<FreebieFormData>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    // Basic phone validation - allows numbers, spaces, dashes, and parentheses
    const phoneRegex = /^[\d\s\-()]{9,}$/;
    return phoneRegex.test(phone.replace(/\s/g, ""));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FreebieFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = "El nombre es requerido";
    }

    if (!formData.email.trim()) {
      newErrors.email = "El email es requerido";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Por favor ingresa un email válido";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "El teléfono es requerido";
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = "Por favor ingresa un teléfono válido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange =
    (field: keyof FreebieFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      }
    };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    try {
      await onDownload(formData);
    } catch (error) {
      console.error("Error downloading freebie:", error);
      setSubmitError(
        "Error al descargar el freebie. Por favor intenta nuevamente."
      );
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#f8f9fa",
        padding: "24px",
        borderRadius: "8px",
        marginTop: "16px",
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        style={{ color: "#1976d2", marginBottom: "8px" }}
      >
        Descarga tu Freebie Gratis
      </Typography>

      <Typography
        variant="body2"
        style={{ color: "#666", marginBottom: "24px" }}
      >
        Completa tus datos para descargar {product?.name}
      </Typography>

      {submitError && (
        <Alert severity="error" style={{ marginBottom: "16px" }}>
          {submitError}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          fullWidth
          label="Nombre completo"
          value={formData.name}
          onChange={handleInputChange("name")}
          error={!!errors.name}
          helperText={errors.name}
          margin="normal"
          required
          disabled={isLoading}
        />

        <TextField
          fullWidth
          label="Email"
          type="email"
          value={formData.email}
          onChange={handleInputChange("email")}
          error={!!errors.email}
          helperText={errors.email}
          margin="normal"
          required
          disabled={isLoading}
        />

        <TextField
          fullWidth
          label="Teléfono"
          value={formData.phone}
          onChange={handleInputChange("phone")}
          error={!!errors.phone}
          helperText={errors.phone}
          margin="normal"
          required
          disabled={isLoading}
          placeholder="Ej: +51 999 888 777"
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          startIcon={
            isLoading ? <CircularProgress size={20} /> : <DownloadIcon />
          }
          disabled={isLoading}
          style={{
            marginTop: "24px",
            padding: "12px 0",
            fontSize: "1.1rem",
            textTransform: "none",
          }}
        >
          {isLoading ? "Descargando..." : "Descarga tu Freebie"}
        </Button>
      </Box>

      <Typography
        variant="caption"
        style={{
          color: "#666",
          marginTop: "16px",
          display: "block",
          fontSize: "12px",
        }}
      >
        Al descargar este freebie, aceptas recibir comunicaciones de{" "}
        {product?.partner?.name || "Evefant"}
      </Typography>
    </div>
  );
};

export default FreebieDownloadForm;
